import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, Home, MapPin, Ticket, Users, XCircle, Compass, Lock, CheckCircle2, RotateCcw, Star, ImagePlus, UtensilsCrossed } from "lucide-react";
import { api, formatApiError, fmtDate, fmtUSD } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n/LanguageContext";
import StatusBadge from "@/components/StatusBadge";
import Modal from "@/components/Modal";
import FoodRequestModal from "@/components/FoodRequestModal";
import { Label, Textarea, PrimaryButton } from "@/components/fields";
import Reveal, { Caption } from "@/components/Reveal";

export default function DashboardPage() {
  const { user, loading } = useAuth();
  const { t } = useLang();
  const [bookings, setBookings] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [villa, setVilla] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [payingId, setPayingId] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [reviewText, setReviewText] = useState("");
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewPhoto, setReviewPhoto] = useState(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [foodPrefill, setFoodPrefill] = useState(null);
  const [foodOpen, setFoodOpen] = useState(false);

  const pickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/reviews/photo", fd);
      setReviewPhoto({ path: data.path, preview: URL.createObjectURL(file) });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPhotoBusy(false);
    }
  };

  const submitReview = async () => {
    if (reviewText.trim().length < 10) return toast.error(t("reviews.reviewPh"));
    if (photoBusy) return toast.error(t("reviews.uploadingPhoto"));
    setReviewBusy(true);
    try {
      await api.post("/reviews", { rating, text: reviewText.trim(), photo: reviewPhoto?.path || "" });
      toast.success(t("reviews.thanks"));
      setReviewOpen(false);
      setReviewText("");
      setRating(5);
      setReviewPhoto(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setReviewBusy(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    Promise.all([api.get("/bookings/mine"), api.get("/enquiries/mine"), api.get("/villa")])
      .then(([b, e, v]) => {
        setBookings(b.data);
        setEnquiries(e.data);
        setVilla(v.data);
      })
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setFetching(false));
  }, [user]);

  if (loading) {
    return <div className="max-w-5xl mx-auto px-4 py-32 text-ink/40 text-sm" data-testid="dashboard-loading">{t("dashboard.loading")}</div>;
  }
  if (!user) {
    return <Navigate to="/auth" state={{ from: "/account" }} replace />;
  }

  const cancel = async (id) => {
    try {
      const { data } = await api.patch(`/bookings/${id}/cancel`);
      setBookings((bs) => bs.map((b) => (b.id === id ? data : b)));
      toast.success(t("dashboard.cancelled"));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const payDeposit = async (booking) => {
    setPayingId(booking.id);
    try {
      const { data } = await api.post("/payments/deposit", { booking_id: booking.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setPayingId(null);
    }
  };

  const payStayDeposit = async (q) => {
    setPayingId(q.id);
    try {
      const { data } = await api.post("/payments/villa-deposit", { enquiry_id: q.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setPayingId(null);
    }
  };

  const depositBadge = (status, id) =>
    status === "paid" ? (
      <span data-testid={`deposit-paid-${id}`} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold bg-teal/10 text-teal">
        <CheckCircle2 size={12} /> {t("dashboard.depositPaid")}
      </span>
    ) : status === "refunded" || status === "partially_refunded" ? (
      <span data-testid={`deposit-refunded-${id}`} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold bg-ink/10 text-ink/50">
        <RotateCcw size={12} /> {t("dashboard.depositRefunded")}
      </span>
    ) : null;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-14 sm:py-20" data-testid="dashboard-page">
      <Reveal>
        <Caption>{t("dashboard.caption")}</Caption>
        <h1 className="font-serif text-4xl sm:text-5xl text-ink mt-4 leading-tight">
          {t("dashboard.hola")} <span className="italic text-terracotta">{user.name.split(" ")[0]}.</span>
        </h1>
        <p className="text-ink/60 mt-3">{t("dashboard.sub")}</p>
      </Reveal>

      {(bookings.length > 0 || enquiries.length > 0) && (
        <Reveal className="mt-10">
          <div className="rounded-3xl bg-gold/10 border border-gold/30 p-6 flex flex-wrap items-center gap-4" data-testid="review-cta-card">
            <div className="w-11 h-11 rounded-2xl bg-gold/20 flex items-center justify-center shrink-0">
              <Star size={20} className="text-gold" />
            </div>
            <p className="text-sm text-ink/70 grow max-w-xl">{t("reviews.subtitle")}</p>
            <button
              data-testid="open-review-btn"
              onClick={() => setReviewOpen(true)}
              className="rounded-full bg-night text-sand px-5 py-2.5 text-xs font-semibold hover:bg-terracotta transition-colors"
            >
              {t("reviews.share")}
            </button>
          </div>
        </Reveal>
      )}

      <section className="mt-14" data-testid="my-bookings-section">
        <div className="flex items-center gap-3 mb-6">
          <Ticket size={20} className="text-terracotta" />
          <h2 className="font-serif text-2xl text-ink">{t("dashboard.bookingsTitle")}</h2>
        </div>
        {fetching ? (
          <p className="text-sm text-ink/40">{t("dashboard.loading")}</p>
        ) : bookings.length === 0 ? (
          <div className="rounded-3xl border-2 border-dashed border-ink/15 p-10 text-center" data-testid="bookings-empty">
            <p className="text-ink/50 text-sm">{t("dashboard.emptyBookings")}</p>
            <Link to="/excursions" data-testid="bookings-empty-cta" className="inline-flex items-center gap-2 mt-5 rounded-full bg-terracotta text-sand px-6 py-3 text-sm font-semibold hover:bg-terracotta-dark transition-colors">
              <Compass size={15} /> {t("dashboard.browse")}
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {bookings.map((b) => (
              <div key={b.id} data-testid={`booking-row-${b.id}`} className="bg-white rounded-3xl border border-terracotta/10 p-6 shadow-[0_2px_16px_rgba(22,27,34,0.05)]">
                <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                  <div className="grow">
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="font-serif text-xl text-ink">{b.excursion_title}</h3>
                      <StatusBadge status={b.status} />
                      {depositBadge(b.deposit_status, b.id)}
                    </div>
                    <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-3 text-xs text-ink/55">
                      <span className="flex items-center gap-1.5"><CalendarDays size={13} /> {fmtDate(b.date)} · {b.time_slot}</span>
                      <span className="flex items-center gap-1.5"><Users size={13} /> {b.guests} {t("dashboard.guests")}</span>
                      <span className="flex items-center gap-1.5"><MapPin size={13} /> {b.pickup_location}</span>
                      <span className="font-mono uppercase tracking-wider">{t("booking.ref")} {b.id.slice(0, 8).toUpperCase()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 sm:flex-col sm:items-end">
                    <p className="font-serif text-2xl text-terracotta">${fmtUSD(b.total_price)}</p>
                    {(b.status === "pending" || b.status === "confirmed") && (
                      <button
                        data-testid={`cancel-booking-btn-${b.id}`}
                        onClick={() => cancel(b.id)}
                        className="text-xs font-semibold text-destructive hover:underline flex items-center gap-1"
                      >
                        <XCircle size={13} /> {t("dashboard.cancel")}
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2.5 mt-5 pt-4 border-t border-ink/5">
                  {(b.deposit_status ?? "unpaid") === "unpaid" && b.status !== "cancelled" && b.status !== "completed" && (
                    <button
                      data-testid={`pay-deposit-btn-${b.id}`}
                      onClick={() => payDeposit(b)}
                      disabled={payingId === b.id}
                      className="rounded-full bg-terracotta text-sand px-5 py-2 text-xs font-semibold hover:bg-terracotta-dark transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
                    >
                      <Lock size={12} /> {payingId === b.id ? t("dashboard.openingCheckout") : t("dashboard.payDeposit", { amount: fmtUSD(b.total_price) })}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="mt-14" data-testid="my-enquiries-section">
        <div className="flex items-center gap-3 mb-6">
          <Home size={20} className="text-teal" />
          <h2 className="font-serif text-2xl text-ink">{t("dashboard.enquiriesTitle")}</h2>
        </div>
        {fetching ? (
          <p className="text-sm text-ink/40">{t("dashboard.loading")}</p>
        ) : enquiries.length === 0 ? (
          <div className="rounded-3xl border-2 border-dashed border-ink/15 p-10 text-center" data-testid="enquiries-empty">
            <p className="text-ink/50 text-sm">{t("dashboard.emptyEnquiries")}</p>
            <Link to="/" data-testid="enquiries-empty-cta" className="inline-block mt-5 rounded-full bg-night text-sand px-6 py-3 text-sm font-semibold hover:bg-terracotta transition-colors">
              {t("dashboard.seeVilla")}
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {enquiries.map((q) => (
              <div key={q.id} data-testid={`enquiry-row-${q.id}`} className="bg-white rounded-3xl border border-terracotta/10 p-6 shadow-[0_2px_16px_rgba(22,27,34,0.05)]">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="grow">
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="font-serif text-xl text-ink">
                        {fmtDate(q.check_in)} → {fmtDate(q.check_out)}
                      </h3>
                      <StatusBadge status={q.status} />
                      {depositBadge(q.deposit_status, q.id)}
                    </div>
                    <p className="text-xs text-ink/55 mt-2">
                      {q.guests} {t("dashboard.guests")}{q.nights ? ` · ${q.nights} ${t("dashboard.nights")}` : ""} · {t("dashboard.sent")} {fmtDate(q.created_at)}
                    </p>
                    {q.message && <p className="text-sm text-ink/60 mt-2 italic">“{q.message}”</p>}
                  </div>
                  {q.quoted_total && (
                    <div className="text-right">
                      <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-ink/40">{t("dashboard.estimate")}</p>
                      <p className="font-serif text-2xl text-terracotta" data-testid={`enquiry-quote-${q.id}`}>${fmtUSD(q.quoted_total)}</p>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2.5 mt-5 pt-4 border-t border-ink/5">
                  <button
                    data-testid={`food-request-stay-${q.id}`}
                    onClick={() => {
                      setFoodPrefill({ reservation_ref: q.id.slice(0, 8).toUpperCase(), start_date: q.check_in, end_date: q.check_out });
                      setFoodOpen(true);
                    }}
                    className="rounded-full border border-terracotta/30 text-terracotta px-5 py-2 text-xs font-semibold hover:bg-terracotta hover:text-sand transition-colors inline-flex items-center gap-1.5"
                  >
                    <UtensilsCrossed size={12} /> {t("food.requestFor")}
                  </button>
                  {q.status === "confirmed" && q.deposit_status === "unpaid" && q.deposit_amount > 0 && (
                    <button
                      data-testid={`pay-stay-deposit-btn-${q.id}`}
                      onClick={() => payStayDeposit(q)}
                      disabled={payingId === q.id}
                      className="rounded-full bg-terracotta text-sand px-5 py-2 text-xs font-semibold hover:bg-terracotta-dark transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
                    >
                      <Lock size={12} /> {payingId === q.id ? t("dashboard.openingCheckout") : t("dashboard.payStayDeposit", { amount: fmtUSD(q.quoted_total) })}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <Modal open={reviewOpen} onClose={() => setReviewOpen(false)} title={t("reviews.shareTitle")} testid="review-modal">
        <div className="space-y-4">
          <div>
            <Label>{t("reviews.yourRating")}</Label>
            <div className="flex gap-1.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" data-testid={`rating-star-${n}`} onClick={() => setRating(n)} className="p-1" aria-label={`${n} stars`}>
                  <Star size={28} fill={n <= rating ? "#E8A838" : "none"} strokeWidth={1.5} className={n <= rating ? "text-gold" : "text-ink/25"} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label>{t("reviews.yourReview")}</Label>
            <Textarea data-testid="review-text-input" value={reviewText} onChange={(e) => setReviewText(e.target.value)} placeholder={t("reviews.reviewPh")} />
          </div>
          <div>
            <Label>{t("reviews.addPhoto")}</Label>
            {reviewPhoto ? (
              <div className="flex items-center gap-3" data-testid="review-photo-preview">
                <img src={reviewPhoto.preview} alt="" className="w-16 h-16 rounded-2xl object-cover" />
                <p className="text-xs text-teal font-medium">{t("reviews.photoReady")}</p>
                <button type="button" data-testid="review-photo-remove" onClick={() => setReviewPhoto(null)} className="text-xs text-destructive hover:underline ml-auto">
                  <XCircle size={14} className="inline mr-1" />{t("reviews.removePhoto")}
                </button>
              </div>
            ) : (
              <label
                data-testid="review-photo-input-label"
                className="flex items-center gap-2 rounded-2xl border-2 border-dashed border-ink/15 px-4 py-3 text-sm text-ink/55 cursor-pointer hover:border-terracotta hover:text-terracotta transition-colors"
              >
                <ImagePlus size={17} />
                {photoBusy ? t("reviews.uploadingPhoto") : t("reviews.addPhoto")}
                <input data-testid="review-photo-input" type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pickPhoto} disabled={photoBusy} />
              </label>
            )}
          </div>
          <PrimaryButton data-testid="review-submit-btn" onClick={submitReview} disabled={reviewBusy} className="w-full">
            {reviewBusy ? t("enquiry.sending") : t("reviews.submit")}
          </PrimaryButton>
        </div>
      </Modal>
      <FoodRequestModal open={foodOpen} onClose={() => setFoodOpen(false)} prefill={foodPrefill} />
    </div>
  );
}
